import { useLayoutEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useAssistantBrowserCommand, useAssistantBrowserCommandDispatcher } from '@/lib/assistant-browser-commands'
import { assistantBrowserCommands } from '@/lib/assistant-browser-commands.generated'
import { AGENT_PERMISSIONS, PERMISSION_KEYS } from '@/lib/permissions'
import { usePermissionsAt } from '@/lib/use-permissions'
import { PREFERRED_COUNTER_POLICY_TYPE } from '../../api/preferred-counter-policy-api'
import { PRINTER_EVENT_RULE_POLICY_TYPE } from '../../api/event-rule-policies-api'
import { usePrinterPolicyDefinitionOptions } from './printer-policy-definition-query'

export function usePrinterPolicyGroup(entityId: string, valueType: string, enabled: boolean, selectedPolicyId?: string) {
  const [group, setGroup] = useState({ expanded: true, selectedPolicyId })
  if (group.selectedPolicyId !== selectedPolicyId) {
    setGroup({ expanded: selectedPolicyId ? true : group.expanded, selectedPolicyId })
  }
  function setExpanded(expanded: boolean) {
    setGroup({ expanded, selectedPolicyId })
  }
  useAssistantBrowserCommand(assistantBrowserCommands.printerPolicyGroup, (command) => {
    setExpanded(command.expanded)
    return true
  }, (command) => enabled && command.entityId === entityId && command.valueType === valueType)
  return { expanded: group.expanded, setExpanded }
}

export function usePrinterPolicySelection(
  entityId: string,
  selectedPolicyId: string | undefined,
  select: (policyId: string | undefined) => Promise<void>,
) {
  const { can } = usePermissionsAt(entityId)
  const client = useQueryClient()
  const dispatch = useAssistantBrowserCommandDispatcher()
  const optionsFor = usePrinterPolicyDefinitionOptions(entityId)
  const current = useRef<{ optionsFor: typeof optionsFor; selectedPolicyId: string | undefined } | null>(null)
  useLayoutEffect(() => {
    current.current = { optionsFor, selectedPolicyId }
    return () => { current.current = null }
  }, [optionsFor, selectedPolicyId])
  useAssistantBrowserCommand(assistantBrowserCommands.printerPolicySelection, async (command, signal) => {
    if (signal.aborted) return false
    try {
      const policyId = command.policyId.toLowerCase()
      const options = optionsFor(policyId)
      function requestIsCurrent() {
        const latest = current.current
        return !signal.aborted && latest?.selectedPolicyId === selectedPolicyId
          && JSON.stringify(options.queryKey) === JSON.stringify(latest?.optionsFor(policyId).queryKey)
      }
      const definition = await client.fetchQuery({ ...options, staleTime: 0 })
      if (!requestIsCurrent() || !definition || definition.valueType !== command.valueType) return false
      if (command.expanded) {
        const opened = await dispatch({ type: 'printer_policy_group', payload: {
          entityId, valueType: command.valueType, expanded: true,
        } }, signal)
        if (!opened || !requestIsCurrent()) return false
        await select(policyId)
      }
      else if (selectedPolicyId === policyId) await select(undefined)
      return true
    } catch {
      return false
    }
  }, (command) => command.entityId.toLowerCase() === entityId && can(AGENT_PERMISSIONS.read)
    && (command.valueType === PREFERRED_COUNTER_POLICY_TYPE
      || (command.valueType === PRINTER_EVENT_RULE_POLICY_TYPE && can(PERMISSION_KEYS.alertRead))))
}
